import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { DISPUTE_TYPES, disputeLabel, disputeTooltip } from '../lib/disputeTypes.js';

function TipIcon({ text, label }) {
  const [show, setShow] = useState(false);

  return (
    <span
      className="dispute-type-tip"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocus={() => setShow(true)}
      onBlur={() => setShow(false)}
    >
      <button
        type="button"
        className="dispute-type-tip-btn"
        aria-label={`About ${label}: ${text}`}
        tabIndex={0}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {show ? (
        <div className="dispute-type-tip-bubble" role="tooltip">
          {text}
        </div>
      ) : null}
    </span>
  );
}

export default function DisputeTypeSelect({
  value,
  onChange,
  required = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();
  const selectedLabel = value ? disputeLabel(value) : 'Select dispute type';
  const selectedTip = disputeTooltip(value);
  const known = DISPUTE_TYPES.some((item) => item.value === value);

  useLayoutEffect(() => {
    if (!open || disabled || !rootRef.current) {
      setDropUp(false);
      return undefined;
    }

    function updatePlacement() {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Approx menu height for 10 compact options (+ padding)
      const menuEstimate = Math.min(DISPUTE_TYPES.length * 40 + 16, window.innerHeight * 0.7);
      setDropUp(spaceBelow < menuEstimate && spaceAbove > spaceBelow);
    }

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);
    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [open, disabled]);

  useEffect(() => {
    if (!open || disabled) return undefined;
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, disabled]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  function choose(next) {
    if (disabled) return;
    onChange(next);
    setOpen(false);
  }

  return (
    <div
      className={`dispute-type-select${dropUp ? ' is-drop-up' : ''}${
        disabled ? ' is-disabled' : ''
      }`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`dispute-type-trigger${open ? ' is-open' : ''}${
          !value ? ' is-placeholder' : ''
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <span>{selectedLabel === '—' ? value : selectedLabel}</span>
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
          <path
            d="M4 6l4 4 4-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Native select kept for required/validation; visually hidden and out of layout */}
      <select
        className="sr-only dispute-type-native"
        tabIndex={-1}
        aria-hidden="true"
        required={required}
        disabled={disabled}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">Select dispute type</option>
        {DISPUTE_TYPES.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
        {value && !known ? <option value={value}>{disputeLabel(value)}</option> : null}
      </select>

      {open && !disabled ? (
        <ul className="dispute-type-menu" role="listbox" id={listId}>
          {DISPUTE_TYPES.map((item) => {
            const active = item.value === value;
            return (
              <li key={item.value} className="dispute-type-option" role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  className={`dispute-type-option-pick${active ? ' is-selected' : ''}`}
                  onClick={() => choose(item.value)}
                >
                  <span className="dispute-type-option-label">{item.label}</span>
                </button>
                <TipIcon text={item.tooltip} label={item.label} />
              </li>
            );
          })}
        </ul>
      ) : null}

      {selectedTip ? <small className="field-hint">{selectedTip}</small> : null}
    </div>
  );
}
